import { VertexArrayObject } from '../utils/VertexArrayObject';
import { Renderer } from './Renderer';
import { gl } from '../../Engine';

/**
 * A lazy, process-wide cache of {@link Renderer Renderers} (and with them their
 * compiled {@link ShaderProgram shader programs}), keyed by renderer class. The
 * first {@link getRenderer} for a class constructs and compiles; every later one
 * hands back the same instance. Compiling a shader is expensive, so the cache is
 * the reason a renderer should be reached through here rather than constructed
 * per draw — that, not lifecycle management, is what RenderStorage buys.
 *
 * **Lifetime is the document's, not this class's.** Nothing in the application
 * calls {@link RenderStorage.delete}, so in practice every cached renderer and the
 * {@link quadVAO} live until the page goes away and the GL context is torn down
 * with it. {@link delete} is the designated teardown hook — correct, and exercised
 * by the WebGL browser tests, which use it to rebuild after a forced context loss —
 * but it is currently unwired in production. Treat it as the seam to call from if a
 * context-loss handler or a multi-document host is ever added, not as something
 * that runs today. In particular, do not rely on a {@link Renderer.delete} ever
 * being reached to release a resource: acquire nothing here that would matter if
 * it were never freed.
 */

export class RenderStorage {
    /**
     * {@link RenderStorage} singleton. There may only ever be one active RenderStorage at any given moment.
     */
    private static storage: RenderStorage = new RenderStorage();

    /**
     * Map holding reference to all the {@link Renderer renderers} that have been loaded via this RenderStorage.\
     * This RenderStorage is responsible to free the memory allocated by those Renderers when WebGL context is terminated.
     */
    private renderers: Map<RendererClass<Renderer>, Renderer>;

    /**
     * Simple {@link VertexArrayObject} that contains a single {@link VertexBufferObject} of type\
     * {@link AttributeVertexBufferObject} with values {-1, 1}, {-1, -1}, {1, 1} and {1, -1}.
     *
     * @example <caption>To render the data stored in the quadVAO one may use</caption>
     * quadVAO.bind(0);
     * gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
     * quadVAO.unbind(0);
     *
     * @example <caption>Those vertex coordinates can be converted to 2D texture coordinates within VERTEX_SHADER using</caption>
     * vec2 texCoords = (vecCoords + vec(1.0, 1.0)) * 0.5;
     *
     */
    private quadVAO?: VertexArrayObject;

    /**
     * Private constructor that initializes an empty map of {@link Renderer Renderers}.
     */
    private constructor() {
        this.renderers = new Map<RendererClass<Renderer>, Renderer>();
    }

    /**
     * Method to get an instance of renderer class
     * @param rendererClass The Renderer class instance of which is to be retrieved
     * @returns The renderer of requested type
     */
    private getRenderer<T extends Renderer>(
        rendererClass: RendererClass<T>
    ): T {
        const existing: Renderer | undefined =
            this.renderers.get(rendererClass);
        if (existing) {
            return existing as T;
        } else {
            const renderer = new rendererClass();
            this.renderers.set(rendererClass, renderer);
            return renderer;
        }
    }

    /**
     * Note: The user should NOT delete the {@link Renderer} provided by this method.\
     * The lifecycle of the provided Renderer is handled by the provider.
     * @description Method to get an instance of renderer class
     * @param rendererClass The Renderer class instance of which is to be retrieved
     * @returns The renderer of requested type
     */
    public static getRenderer<T extends Renderer>(
        rendererClass: RendererClass<T>
    ): T {
        return this.storage.getRenderer<T>(rendererClass);
    }

    /**
     * Method to {@link Renderer.delete delete} the {@link Renderer renderers} and {@link RenderStorage.quadVAO quadVAO} stored within this {@link RenderStorage}.\
     * Leaves the storage empty, so the next {@link getRenderer}/{@link getQuadVAO} rebuilds.
     *
     * No production caller: see the class doc. Currently reached only from the
     * WebGL browser tests, which call it to rebuild after a forced context loss.
     */
    public static delete(): void {
        if (RenderStorage.storage.quadVAO) {
            RenderStorage.storage.quadVAO.delete();
            // Drop the reference too: the GL object is gone, so a later
            // getQuadVAO must rebuild rather than hand back a dead VAO
            // (e.g. after the context is lost and reinitialized).
            RenderStorage.storage.quadVAO = undefined;
        }
        for (const renderer of RenderStorage.storage.renderers.values()) {
            renderer.delete();
        }
        RenderStorage.storage.renderers.clear();
    }

    /**
     * Note: The user should NOT delete the {@link VertexArrayObject} provided by this method.\
     * The lifecycle of the provided VAO is handled by the provider.
     *
     * @description Method to get reference to {@link RenderStorage.quadVAO},\
     * which is a simple {@link VertexArrayObject} that contains a single\
     * {@link AttributeVertexBufferObject AttributeVBO} with values {-1, 1}, {-1, -1}, {1, 1} and {1, -1}.
     *
     * This sort of {@link VertexArrayObject} is very useful as it can be used to efficiently render 2D data.\
     * Such VAO may be used by many different shaders.\
     * And having multiple VAOs with identical data is not ideal.
     *
     * For the very first request this method generates a quadVAO.\
     * But all the subsequent calls will get reference to that same object.
     *
     * @example <caption>To render the data stored in the quadVAO one may use</caption>
     * quadVAO.bind(0);
     * gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
     * quadVAO.unbind(0);
     *
     * @example <caption>Those vertex coordinates can be converted to 2D texture coordinates within VERTEX_SHADER using</caption>
     * vec2 texCoords = (vecCoords + vec(1.0, 1.0)) * 0.5;
     *
     * @returns Reference to {@link quadVAO} {@link VertexArrayObject}
     */
    public static getQuadVAO(): VertexArrayObject {
        if (!RenderStorage.storage.quadVAO) {
            const vao = new VertexArrayObject();
            vao.bind();
            vao.createAttribute(
                0,
                gl.FLOAT,
                [-1.0, 1.0, -1.0, -1.0, 1.0, 1.0, 1.0, -1.0],
                2
            );
            vao.unbind();
            RenderStorage.storage.quadVAO = vao;
        }
        return RenderStorage.storage.quadVAO;
    }
}

/**
 * All the Renderer classes must have some sort of constructor and that constructor is an instance of RendererClass
 */
type RendererClass<T extends Renderer> = new (...args: unknown[]) => T;
